/**
 * PII hashing for the Holostaff SDK.
 *
 * Uses the browser's Web Crypto subtle.digest (SHA-256). When the
 * environment does not have crypto.subtle (e.g., non-HTTPS iframe),
 * the SDK falls back to skipping the PII value rather than sending it
 * unhashed — safer default.
 */

async function sha256Hex(text: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle
  if (!subtle) return '' // silent skip — caller treats empty as "unavailable"
  const buffer = new TextEncoder().encode(text)
  const digest = await subtle.digest('SHA-256', buffer)
  const bytes = new Uint8Array(digest)
  let hex = ''
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0')
  }
  return hex
}

/** Hash an email address. Lower-cases before hashing for determinism. */
export function hashEmail(email: string): Promise<string> {
  return sha256Hex(email.trim().toLowerCase())
}

/** Hash a display name. Normalises whitespace and case for determinism. */
export function hashName(name: string): Promise<string> {
  return sha256Hex(name.trim().replace(/\s+/g, ' ').toLowerCase())
}

/** Fields in trait/property payloads that are treated as PII by default. */
export const PII_FIELDS = ['email', 'name', 'full_name', 'phone'] as const

/**
 * Walk a traits/properties object and replace recognised PII fields
 * with their hashed values. Unknown fields are left alone — the caller
 * is responsible for not stuffing PII into free-form keys.
 */
export async function hashPiiFields<T extends Record<string, unknown>>(obj: T): Promise<T> {
  if (!obj) return obj
  const out: Record<string, unknown> = { ...obj }
  for (const field of PII_FIELDS) {
    const value = out[field]
    if (typeof value !== 'string' || !value) continue
    const hashed = await (field === 'phone' ? sha256Hex(value) : sha256Hex(value.trim().toLowerCase()))
    if (hashed) out[field] = hashed
  }
  return out as T
}
